import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema, { getFAQSchema } from '@/components/feature/SEOSchema';
import { siteInfo } from '@/mocks/siteInfo';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');

    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('消息已发送，我们会尽快回复！');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9te4v1anrhn4bgf7g9g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([key, value]) => [key, String(value)])
        ),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('消息已发送，我们会尽快回复！');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(
          serverMsg && !serverMsg.includes('spam')
            ? serverMsg
            : '发送失败，请稍后再试或通过邮箱联系我们。'
        );
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试。');
    }

    setTimeout(() => {
      setFormStatus('idle');
      setFormMessage('');
    }, 5000);
  };

  const faqSchemaData = siteInfo.faqs.map((f) => ({
    question: f.question,
    answer: f.answer,
  }));

  return (
    <>
      <SEOSchema type="faq" data={faqSchemaData as unknown as Record<string, unknown>} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(getFAQSchema(faqSchemaData)),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '关于我们 - 偷拍自拍技巧大全',
            description: siteInfo.description,
            url: 'https://999vv.cn/about',
            inLanguage: 'zh-CN',
            isPartOf: {
              '@type': 'WebSite',
              name: '偷拍自拍技巧大全',
              url: 'https://999vv.cn',
            },
          }),
        }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="relative w-full h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20creative%20photography%20studio%20workspace%20with%20natural%20lighting%2C%20minimal%20modern%20interior%2C%20camera%20and%20prints%20on%20wooden%20desk%2C%20soft%20ambient%20atmosphere%2C%20clean%20neutral%20aesthetic%2C%20artistic%20lifestyle%2C%20editorial%20quality&width=1800&height=800&seq=about-hero-01&orientation=landscape"
            alt="关于偷拍自拍技巧大全"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-2xl mx-auto">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-white/75">
              用镜头记录生活中最真实、最动人的瞬间
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="max-w-3xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              品牌故事
            </h2>
          </div>
          <div className="space-y-5 text-foreground-700 leading-relaxed text-sm md:text-base">
            <p>
              偷拍自拍技巧大全诞生于一个简单的想法：<strong>最好的照片不是"摆"出来的，而是捕捉到的。</strong>
            </p>
            <p>
              我们是一群热爱摄影的内容创作者。在日常拍摄中我们发现，那些最打动人的照片，
              往往不是精心摆拍的结果，而是某个不经意的瞬间——你在咖啡馆里低头看书时的一道侧影，
              在街头转身回眸时的一缕发丝，在窗边发呆时被阳光勾勒出的轮廓。
            </p>
            <p>
              这些"偷拍自拍"的画面，有着普通自拍难以企及的故事感和真实感。于是，我们开始系统地
              研究和整理相关的技巧——从构图到光线，从设备到后期，从姿势到场景——并把它们分享给
              更多热爱摄影的朋友。
            </p>
            <p>
              从{siteInfo.founded}至今，我们已经积累了超过
              <strong>{siteInfo.stats.articles}篇</strong>精心编写的教程，
              吸引了<strong>{siteInfo.stats.readers.toLocaleString()}+</strong>位摄影爱好者。
              我们始终相信，每个人都有能力拍出好看的照片——你需要的不是昂贵的设备，而是正确的方法
              和一点点"发现美"的眼睛。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-background-100 border-y border-background-200/60">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-12">
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500">
                  {siteInfo.stats.articles}+
                </div>
                <div className="text-sm text-foreground-500 mt-2">精选教程</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-accent-500">
                  {(siteInfo.stats.readers / 10000).toFixed(1)}万+
                </div>
                <div className="text-sm text-foreground-500 mt-2">摄影爱好者</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-secondary-500">
                  {siteInfo.stats.categories}
                </div>
                <div className="text-sm text-foreground-500 mt-2">分类专题</div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="max-w-5xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-600">听听他们怎么说</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {siteInfo.testimonials.map((t) => (
              <div
                key={t.name}
                className="bg-background-50 rounded-lg border border-background-200/60 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full overflow-hidden shrink-0">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="font-medium text-sm text-foreground-900">{t.name}</div>
                    <div className="flex items-center gap-0.5 mt-0.5">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-[10px] text-primary-400"></i>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
            </div>
            <div className="space-y-3">
              {siteInfo.faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-50 rounded-lg border border-background-200/60 overflow-hidden"
                >
                  <summary className="flex items-center justify-between cursor-pointer px-6 py-4 text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                    {faq.question}
                    <div className="w-5 h-5 flex items-center justify-center shrink-0 ml-3">
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </div>
                  </summary>
                  <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="max-w-3xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600">
              有任何问题或建议？欢迎随时与我们联系
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            <div className="bg-background-100 rounded-lg p-5 text-center">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-mail-line text-lg text-primary-500"></i>
              </div>
              <div className="text-xs text-foreground-500 mb-1">邮箱</div>
              <div className="text-sm text-foreground-900 font-medium">{siteInfo.email}</div>
            </div>
            <div className="bg-background-100 rounded-lg p-5 text-center">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-wechat-line text-lg text-accent-500"></i>
              </div>
              <div className="text-xs text-foreground-500 mb-1">微信</div>
              <div className="text-sm text-foreground-900 font-medium">{siteInfo.wechat}</div>
            </div>
            <div className="bg-background-100 rounded-lg p-5 text-center">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-red-packet-line text-lg text-secondary-500"></i>
              </div>
              <div className="text-xs text-foreground-500 mb-1">小红书</div>
              <div className="text-sm text-foreground-900 font-medium">{siteInfo.xiaohongshu}</div>
            </div>
          </div>

          <form
            id="about-contact-form"
            data-readdy-form="true"
            onSubmit={handleSubmit}
            className="bg-background-100 rounded-lg border border-background-200/60 p-6 md:p-8"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="你的名字"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                主题
              </label>
              <input
                id="contact-subject"
                name="subject"
                type="text"
                required
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                placeholder="消息主题"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                消息
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={5}
                required
                maxLength={500}
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                placeholder="请输入你想说的话（最多500字）"
              ></textarea>
              <div className="text-xs text-foreground-400 mt-1 text-right">
                最多500字符
              </div>
            </div>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />

            <button
              type="submit"
              className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              发送消息
            </button>

            {formStatus !== 'idle' && (
              <div
                className={`mt-4 text-sm text-center px-4 py-3 rounded-lg ${
                  formStatus === 'success'
                    ? 'bg-accent-50 text-accent-700'
                    : 'bg-red-50 text-red-700'
                }`}
              >
                {formMessage}
              </div>
            )}
          </form>
        </section>
      </main>

      <Footer />
    </>
  );
}